import Constants from 'expo-constants';
import { useCallback, useState } from 'react';

import {
  ApplePayValidationDocument,
  type IApplePayValidationQuery,
  type IApplePayValidationQueryVariables,
} from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { PaymentProcessor } from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import { client as legacyClient } from '@fhs-legacy/frontend/src/state/graphql/client';
import logger from '@fhs-legacy/frontend/src/utils/logger';

import type { ApplePayDetails, IAppleConfig } from '../../../cart/src/payment/payment-types';

declare global {
  interface Window {
    eProtect: any;
  }
}

interface ApplePayForReloadConfig {
  processor: PaymentProcessor;
}

interface IWorldpayConfig {
  eProtectUrl: string;
  paypageId: string;
}

interface PaymentConfig {
  region: 'US' | 'CA';
  currencyCode: string;
  totalAmount: string;
  merchantId: string;
  payeeName: string;
}

interface BillingAddress {
  locality: string;
  postalCode: string;
  region: string;
  streetAddress: string;
}

export function useApplePayForReload(config: ApplePayForReloadConfig) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const region: 'US' | 'CA' = Constants?.expoConfig?.extra?.eas?.buildRegion ?? 'US';

  const getConfiguration = useCallback((): IAppleConfig | null => {
    const appleConfig: IAppleConfig = Constants?.expoConfig?.extra?.eas?.apple;
    if (!appleConfig) {
      logger.error({ message: '[ApplePay Reload Web] Configuration not found' });
      return null;
    }
    return appleConfig;
  }, []);

  const buildPaymentConfig = useCallback(
    (amount: number, appleConfig: IAppleConfig): PaymentConfig => {
      const currencyCode = region === 'CA' ? 'CAD' : 'USD';
      const totalAmount = (amount / 100).toString();
      const merchantId = appleConfig.prepaidMerchantId || appleConfig.merchantId;

      return {
        region,
        currencyCode,
        totalAmount,
        merchantId,
        payeeName: 'Firehouse Subs',
      };
    },
    [region]
  );

  const parseBillingAddress = useCallback(
    (billingContact?: ApplePayJS.ApplePayPaymentContact): BillingAddress => {
      if (!billingContact) {
        return { locality: '', postalCode: '', region: '', streetAddress: '' };
      }

      const streetAddress = billingContact.addressLines?.[0] ?? '';
      const rawPostalCode = billingContact.postalCode || '';
      const sanitizedPostalCode = rawPostalCode.replace(/[^A-Za-z0-9]/g, '');

      return {
        locality: billingContact.locality || '',
        postalCode: sanitizedPostalCode,
        region: billingContact.administrativeArea || '',
        streetAddress,
      };
    },
    []
  );

  const formatBillingAddress = useCallback((address: BillingAddress): string => {
    return [address.streetAddress, address.locality, address.region, address.postalCode]
      .filter(Boolean)
      .join(', ');
  }, []);

  const validateMerchant = useCallback(
    async (
      validationURL: string,
      merchantId: string
    ): Promise<IApplePayValidationQuery['applePayValidation']> => {
      const variables: IApplePayValidationQueryVariables = {
        merchantIdentifier: merchantId,
        displayName: 'Firehouse Subs',
        validationUrl: validationURL,
        initiative: 'web',
        initiativeContext: typeof window !== 'undefined' ? window.location.host : '',
      };

      const result = await legacyClient.query<
        IApplePayValidationQuery,
        IApplePayValidationQueryVariables
      >({
        query: ApplePayValidationDocument,
        variables,
        fetchPolicy: 'no-cache',
      });

      if (!result?.data?.applePayValidation) {
        throw new Error('Failed to obtain merchant session from Apple');
      }

      return result.data.applePayValidation;
    },
    []
  );

  const parsePaymentResponse = useCallback(
    (payment: ApplePayJS.ApplePayPayment, merchantId: string): ApplePayDetails => {
      const { token, billingContact } = payment;
      const { paymentData, paymentMethod } = token;

      const paymentDataString = JSON.stringify(paymentData);

      const tokenData = paymentData as any;
      const { signature, version, data } = tokenData;
      const { ephemeralPublicKey, publicKeyHash, transactionId } = tokenData.header || {};

      const billingAddress = parseBillingAddress(billingContact);
      const formattedAddress = formatBillingAddress(billingAddress);

      return {
        signature: signature || '',
        data: data || btoa(paymentDataString),
        version,
        ephemeralPublicKey: ephemeralPublicKey || '',
        publicKeyHash: publicKeyHash || '',
        transactionId: transactionId || '',
        decryptAlias: merchantId,
        applicationData: '',
        country: region,
        formatted: formattedAddress,
        paymentData: paymentDataString,
        primary: true,
        type: 'home',
        billingAddress,
        PaymentMethodData: {
          paymentType: paymentMethod.network ?? '',
          displayName: paymentMethod.displayName,
        },
      };
    },
    [region, parseBillingAddress, formatBillingAddress]
  );

  const requestApplePayForReload = useCallback(
    async (amountInCents: number): Promise<ApplePayDetails | null> => {
      setIsLoading(true);
      setError(null);

      try {
        const appleConfig = getConfiguration();
        if (!appleConfig) {
          throw new Error('Apple Pay configuration not found');
        }

        if (typeof window === 'undefined' || !window.ApplePaySession) {
          throw new Error('Apple Pay is not available in this browser');
        }

        if (!ApplePaySession.canMakePayments()) {
          setError('Apple Pay is not available on this device');
          return null;
        }

        const paymentConfig = buildPaymentConfig(amountInCents, appleConfig);

        if (!paymentConfig.merchantId) {
          throw new Error('Apple Pay merchant ID not configured');
        }

        const merchantId = paymentConfig.merchantId;
        const countryCode = paymentConfig.region === 'CA' ? 'CA' : 'US';
        const supportedNetworks = appleConfig.paymentsNetworks ?? [];

        const paymentRequest: ApplePayJS.ApplePayPaymentRequest = {
          countryCode,
          currencyCode: paymentConfig.currencyCode,
          supportedNetworks,
          merchantCapabilities: ['supports3DS'],
          total: {
            label: paymentConfig.payeeName,
            amount: paymentConfig.totalAmount,
          },
          requiredBillingContactFields: ['postalAddress'],
        };

        const applePayDetails = await new Promise<ApplePayDetails>((resolve, reject) => {
          const session = new ApplePaySession(1, paymentRequest);

          session.onvalidatemerchant = async event => {
            try {
              const merchantSession = await validateMerchant(event.validationURL, merchantId);
              session.completeMerchantValidation(merchantSession);
            } catch (err) {
              logger.error({
                message: '[ApplePay Reload Web] Merchant validation failed',
                error: err,
              });
              session.abort();
              reject(new Error('Merchant validation failed'));
            }
          };

          session.onpaymentauthorized = event => {
            try {
              const details = parsePaymentResponse(event.payment, merchantId);
              session.completePayment(ApplePaySession.STATUS_SUCCESS);
              resolve(details);
            } catch (err) {
              session.completePayment(ApplePaySession.STATUS_FAILURE);
              reject(err);
            }
          };

          session.oncancel = () => {
            reject(new Error('User cancelled Apple Pay'));
          };

          session.begin();
        });

        if (config.processor === PaymentProcessor.WORLDPAY) {
          const lowValueToken = await convertToWorldpayTokenWeb(applePayDetails, region);
          return { ...applePayDetails, paymentData: lowValueToken };
        }

        return applePayDetails;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';

        if (errorMessage === 'User cancelled Apple Pay') {
          return null;
        }

        logger.error({ message: '[ApplePay Reload Web] Payment failed', error: errorMessage });
        setError(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [
      getConfiguration,
      buildPaymentConfig,
      parsePaymentResponse,
      validateMerchant,
      config.processor,
      region,
    ]
  );

  return {
    requestApplePayForReload,
    isLoading,
    error,
  };
}

function getWorldpayConfig(): IWorldpayConfig {
  const eas = Constants?.expoConfig?.extra?.eas;
  const worldpayConfig = eas?.US?.worldpay || eas?.CA?.worldpay;

  if (!worldpayConfig?.eProtectUrl || !worldpayConfig?.paypageId) {
    throw new Error('Worldpay eProtect configuration not found');
  }

  return {
    eProtectUrl: worldpayConfig.eProtectUrl,
    paypageId: worldpayConfig.paypageId,
  };
}

function loadScript(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${url}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = url;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load eProtect SDK from ${url}`));
    document.head.appendChild(script);
  });
}

async function convertToWorldpayTokenWeb(
  applePayDetails: ApplePayDetails,
  region: string
): Promise<string> {
  const worldpayConfig = getWorldpayConfig();
  const env = (Constants?.expoConfig?.extra?.eas?.env ?? '') as string;
  const merchantReportGroup = `${env}-FHS-${region}`.toUpperCase();
  const merchantOrderId = `GCRELOAD-${Date.now()}`.slice(0, 20);

  await loadScript(`${worldpayConfig.eProtectUrl}/eProtect/js/eProtect-api3.js`);

  if (typeof window.eProtect !== 'function') {
    throw new Error('eProtect SDK not available after loading script');
  }

  return new Promise<string>((resolve, reject) => {
    const eProtectClient = new window.eProtect();

    const request = {
      paypageId: worldpayConfig.paypageId,
      reportGroup: merchantReportGroup,
      orderId: merchantOrderId,
      id: merchantOrderId,
      'applepay.data': applePayDetails.data ?? '',
      'applepay.signature': applePayDetails.signature ?? '',
      'applepay.version': applePayDetails.version ?? '',
      'applepay.header.applicationData': '',
      'applepay.header.ephemeralPublicKey': applePayDetails.ephemeralPublicKey ?? '',
      'applepay.header.publicKeyHash': applePayDetails.publicKeyHash ?? '',
      'applepay.header.transactionId': applePayDetails.transactionId ?? '',
    };

    const timeoutMs = 15000;

    eProtectClient.sendToEprotect(
      request,
      {},
      (response: any) => {
        if (!response?.paypageRegistrationId) {
          reject(new Error(`eProtect error: ${response?.message || 'no registration ID'}`));
          return;
        }
        resolve(response.paypageRegistrationId);
      },
      (response: any) => {
        reject(new Error(`eProtect error: ${response?.message || 'unknown failure'}`));
      },
      timeoutMs
    );
  });
}
